/**
 * plugins/doc-header.ts — Header formal de documentação (div.page-header + .header-top + .header-body)
 *
 * Ferramentas: editar brand, meta do documento, badge card, título, subtítulo,
 *              info-items (label/valor) — adicionar, mover, excluir.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, closestInside, moveSibling } from '../types';

export class DocHeaderPlugin implements VtbPlugin {
    readonly panelId = 'vtb-doc-header';
    readonly selectorHints = ['.page-header', 'div.page-header', '[data-vtb-id="page-header"]'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const el = closestInside(target, visualEditor, e =>
            e.classList.contains('page-header') || e.dataset.vtbId === 'page-header'
        );
        if (!el) return null;

        // Detecta se o clique foi num info-item específico
        const infoItem = closestInside(target, el, e => 
            e.classList.contains('header-info-item') || e.dataset.vtbId === 'header-info-item'
        );

        return {
            type: this.panelId,
            label: 'Cabeçalho',
            el,
            container: el.querySelector<HTMLElement>('.header-info-row') ?? 
                       el.querySelector<HTMLElement>('[data-vtb-id="header-info-row"]') ?? 
                       undefined,
            extra: { infoItem: infoItem ?? null },
        };
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const el = ctx.el;

        // Preenche campos do painel
        const brand    = el.querySelector<HTMLElement>('[data-vtb-id="brand"]')    ?? el.querySelector<HTMLElement>('.header-brand');
        const meta     = el.querySelector<HTMLElement>('[data-vtb-id="meta"]')     ?? el.querySelector<HTMLElement>('.header-meta');
        const badge    = el.querySelector<HTMLElement>('[data-vtb-id="badge"]')    ?? el.querySelector<HTMLElement>('.badge-card');
        const title    = el.querySelector<HTMLElement>('[data-vtb-id="title"]')    ?? el.querySelector<HTMLElement>('.header-title');
        const subtitle = el.querySelector<HTMLElement>('[data-vtb-id="subtitle"]') ?? el.querySelector<HTMLElement>('.header-subtitle');

        this._setVal(registry, 'vtb-dh-brand',    brand?.textContent ?? '');
        this._setVal(registry, 'vtb-dh-meta',     meta?.innerHTML?.replace(/<br\s*\/?>/gi, '\n') ?? '');
        this._setVal(registry, 'vtb-dh-badge',    badge?.textContent ?? '');
        this._setVal(registry, 'vtb-dh-title',    title?.textContent ?? '');
        this._setVal(registry, 'vtb-dh-subtitle', subtitle?.innerHTML ?? '');

        // Atualiza contagem de info-items
        this._updateInfoCount(registry, ctx);

        // Mostra/esconde form de add info
        const addForm = registry.q<HTMLElement>('vtb-dh-info-add-form');
        if (addForm) addForm.style.display = 'none';
    }

    setup(registry: IVtbRegistry): void {
        // Apply geral
        registry.q('vtb-dh-apply')?.addEventListener('click', () => this._apply(registry));

        // Info-item: add
        registry.q('vtb-dh-info-add-open')?.addEventListener('click', () => {
            const f = registry.q<HTMLElement>('vtb-dh-info-add-form');
            if (f) f.style.display = f.style.display === 'none' ? 'flex' : 'none';
        });
        registry.q('vtb-dh-info-confirm')?.addEventListener('click', () => this._addInfoItem(registry));
        registry.q('vtb-dh-info-cancel')?.addEventListener('click', () => {
            const f = registry.q<HTMLElement>('vtb-dh-info-add-form');
            if (f) f.style.display = 'none';
        });

        // Info-item: mover/excluir
        registry.q('vtb-dh-info-up')?.addEventListener('click',   () => this._moveInfoItem(registry, -1));
        registry.q('vtb-dh-info-down')?.addEventListener('click', () => this._moveInfoItem(registry,  1));
        registry.q('vtb-dh-info-del')?.addEventListener('click',  () => this._deleteInfoItem(registry));
    }

    // ── Apply: atualiza textos principais ─────────────────────────────────────

    private _apply(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const el = ctx.el;

        const brand    = el.querySelector<HTMLElement>('[data-vtb-id="brand"]')    ?? el.querySelector<HTMLElement>('.header-brand');
        const meta     = el.querySelector<HTMLElement>('[data-vtb-id="meta"]')     ?? el.querySelector<HTMLElement>('.header-meta');
        const badge    = el.querySelector<HTMLElement>('[data-vtb-id="badge"]')    ?? el.querySelector<HTMLElement>('.badge-card');
        const title    = el.querySelector<HTMLElement>('[data-vtb-id="title"]')    ?? el.querySelector<HTMLElement>('.header-title');
        const subtitle = el.querySelector<HTMLElement>('[data-vtb-id="subtitle"]') ?? el.querySelector<HTMLElement>('.header-subtitle');

        const vBrand    = registry.q<HTMLInputElement>('vtb-dh-brand')?.value ?? '';
        const vMeta     = registry.q<HTMLTextAreaElement>('vtb-dh-meta')?.value ?? '';
        const vBadge    = registry.q<HTMLInputElement>('vtb-dh-badge')?.value ?? '';
        const vTitle    = registry.q<HTMLInputElement>('vtb-dh-title')?.value ?? '';
        const vSubtitle = registry.q<HTMLInputElement>('vtb-dh-subtitle')?.value ?? '';

        if (brand)    brand.textContent = vBrand;
        if (meta)     meta.innerHTML    = vMeta.replace(/\n/g, '<br />');
        if (badge)    badge.textContent = vBadge;
        if (title)    title.textContent = vTitle;
        if (subtitle) subtitle.innerHTML = vSubtitle;

        registry.sync();
    }

    // ── Info-item: adicionar ──────────────────────────────────────────────────

    private _addInfoItem(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || !ctx.container) return;

        const labelText = registry.q<HTMLInputElement>('vtb-dh-info-label')?.value?.trim() || 'Campo';
        const valueText = registry.q<HTMLInputElement>('vtb-dh-info-value')?.value?.trim() || '';

        const item = document.createElement('div');
        item.className = 'header-info-item';
        item.innerHTML =
            '<span class="label">' + this._esc(labelText) + '</span>' +
            '<span class="value">' + this._esc(valueText) + '</span>';

        ctx.container.appendChild(item);

        // Reset form
        const f = registry.q<HTMLElement>('vtb-dh-info-add-form');
        if (f) f.style.display = 'none';
        const li = registry.q<HTMLInputElement>('vtb-dh-info-label');
        const vi = registry.q<HTMLInputElement>('vtb-dh-info-value');
        if (li) li.value = '';
        if (vi) vi.value = '';

        this._updateInfoCount(registry, ctx);
        registry.sync();
    }

    // ── Info-item: mover ──────────────────────────────────────────────────────

    private _moveInfoItem(registry: IVtbRegistry, dir: -1 | 1): void {
        const item = this._getActiveInfoItem(registry);
        if (item) {
            moveSibling(item, dir);
            registry.sync();
        }
    }

    // ── Info-item: excluir ────────────────────────────────────────────────────

    private _deleteInfoItem(registry: IVtbRegistry): void {
        const item = this._getActiveInfoItem(registry);
        if (item) {
            item.remove();
            const ctx = registry.activeCtx;
            if (ctx) this._updateInfoCount(registry, ctx);
            registry.sync();
        }
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    private _getActiveInfoItem(registry: IVtbRegistry): HTMLElement | null {
        const ctx = registry.activeCtx;
        if (!ctx) return null;
        return (ctx.extra?.infoItem as HTMLElement | null) ?? null;
    }

    private _setVal(registry: IVtbRegistry, id: string, val: string): void {
        const input = registry.q<HTMLInputElement | HTMLTextAreaElement>(id);
        if (input) input.value = val;
    }

    private _updateInfoCount(registry: IVtbRegistry, ctx: SmartCtx): void {
        const row = ctx.container ?? ctx.el.querySelector<HTMLElement>('.header-info-row');
        const count = row?.querySelectorAll('.header-info-item').length ?? 0;
        const chip = registry.q<HTMLElement>('vtb-dh-info-count');
        if (chip) chip.textContent = count + ' campo' + (count !== 1 ? 's' : '');
    }

    private _esc(text: string): string {
        const d = document.createElement('div');
        d.textContent = text;
        return d.innerHTML;
    }
}
